import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChefHat, Loader2 } from 'lucide-react';
import { RecipeDetails } from '@/types/recipe-details';
import { extractErrorMessage, showNotesSavedToast } from '@/utils/toast-utils';
import InlineFeedback from '@/components/shared/InlineFeedback';
import {
  updateDraftRecipeNotes,
  getCustomizedRecipeDetails,
  updateFinalizedMealPlanItem,
} from '@/lib/apiService';

interface InstructionsTabProps {
  recipeDetails: RecipeDetails;
  planId?: string;
  mealPlanId?: string;
  itemId?: string;
  day?: number;
  mealName?: string;
  mealOrder?: number;
  recipeId?: string | number;
  onNotesSaved?: (notes: string | null) => void;
}

type NotesUpdateResponse = {
  success: boolean;
  data?: unknown;
  message?: string;
};

/** Read notes from loaded recipe data. */
function getNotesFromRecipeData(data: unknown): string {
  const d = data as Record<string, unknown> | null | undefined;
  if (!d) return '';
  if (d.notes === null) return '';
  if (typeof d.notes === 'string') return d.notes;
  return '';
}

function readSavedNotesFromResponse(response: NotesUpdateResponse): string | null {
  if (response.success === false) {
    throw new Error(response.message || 'Unable to save notes');
  }

  const data = response.data as Record<string, unknown> | null | undefined;
  if (!data || !Object.prototype.hasOwnProperty.call(data, 'notes')) {
    throw new Error('Notes update response did not include notes.');
  }

  if (data.notes === null) return null;
  if (typeof data.notes === 'string') return data.notes;

  throw new Error('Notes update response included invalid notes.');
}

/** Outlined field with floating label — matches `RecipeDetailsModal` Servings row (Figma-style). */
function NotesOutlinedField({
  id,
  labelId,
  value,
  onChange,
  loading,
  placeholder,
}: {
  id: string;
  labelId: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  loading: boolean;
  placeholder: string;
}) {
  return (
    <div
      className="relative flex min-h-[120px] min-w-0 flex-col rounded border border-[rgba(0,0,0,0.23)] bg-white px-3.5 pb-3 pt-5"
      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
    >
      <span
        id={labelId}
        className="pointer-events-none absolute left-3 top-0 z-[1] max-w-[calc(100%-24px)] -translate-y-1/2 bg-white px-1 text-xs leading-none text-[rgba(0,0,0,0.6)]"
      >
        Notes
      </span>
      {loading ? (
        <div className="flex min-h-[100px] flex-1 items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[rgba(0,0,0,0.38)]" aria-hidden />
          <span className="text-sm text-[rgba(0,0,0,0.45)]">Loading notes...</span>
        </div>
      ) : (
        <textarea
          id={id}
          aria-labelledby={labelId}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={5}
          className="min-h-[100px] w-full resize-y border-0 bg-transparent p-0 text-sm leading-relaxed text-[#111827] outline-none ring-0 placeholder:text-[#99a1af] focus:outline-none focus:ring-0 focus-visible:ring-0"
        />
      )}
    </div>
  );
}

const InstructionsTab: React.FC<InstructionsTabProps> = ({ 
  recipeDetails, 
  planId, 
  mealPlanId,
  itemId,
  day, 
  mealName,
  recipeId,
  onNotesSaved,
}) => {
  const [notes, setNotes] = useState(() => getNotesFromRecipeData(recipeDetails));
  const [saving, setSaving] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [notesFeedback, setNotesFeedback] = useState<string | null>(null);

  // Fetch custom notes when component mounts (and when recipe/draft context changes)
  useEffect(() => {
    const fetchCustomNotes = async () => {
      const effectiveRecipeId = recipeId || recipeDetails.externalRecipeId || recipeDetails.id;
      const effectiveItemId = String(itemId ?? '').trim();
      
      if (planId && day !== undefined && mealName && effectiveRecipeId && effectiveItemId) {
        setLoadingNotes(true);
        try {
          const response = await getCustomizedRecipeDetails(
            String(effectiveRecipeId),
            planId,
            day,
            mealName,
            effectiveItemId,
          );
          
          if (response.success && response.data) {
            setNotes(getNotesFromRecipeData(response.data));
          }
        } catch (error) {
          console.error('Error fetching custom notes:', error);
          // Silently fail - notes will just be empty or keep initial from recipeDetails
        } finally {
          setLoadingNotes(false);
        }
      }
    };

    fetchCustomNotes();
  }, [planId, day, mealName, recipeId, recipeDetails.id, recipeDetails.externalRecipeId]);

  const handleSaveNotes = async () => {
    const effectiveRecipeId = recipeId || recipeDetails.externalRecipeId || recipeDetails.id;
    const trimmedNotes = notes.trim();

    setSaving(true);
    try {
      let response: NotesUpdateResponse;
      if (planId && day !== undefined && mealName && effectiveRecipeId) {
        response = await updateDraftRecipeNotes(planId, day, mealName, String(effectiveRecipeId), trimmedNotes);
      } else if (mealPlanId && itemId) {
        response = await updateFinalizedMealPlanItem(mealPlanId, { itemId, notes: trimmedNotes });
      } else {
        throw new Error('Unable to save notes because required context is missing.');
      }

      const savedNotes = readSavedNotesFromResponse(response);
      setNotes(savedNotes ?? '');
      onNotesSaved?.(savedNotes);
      setNotesFeedback(null);
      showNotesSavedToast('recipe', savedNotes);
    } catch (error) {
      console.error('Error saving notes:', error);
      setNotesFeedback(extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const renderInstructions = () => {
    const rawInstructions = (recipeDetails as RecipeDetails & { instructions?: unknown[] }).instructions ?? recipeDetails.cookingInstructions;
    
    if (rawInstructions && rawInstructions.length > 0) {
      // Support both shapes: API { stepNumber, instruction } and legacy { number, step }
      const parsedInstructions = rawInstructions.map((instruction: any, index) => ({
        number: instruction.stepNumber ?? instruction.number ?? index + 1,
        step: instruction.instruction ?? instruction.step ?? ''
      }));
      
      return (
        <div className="space-y-6">
          {/* Source Link Section - Moved to top */}
          {(recipeDetails.sourceUrl || recipeDetails.recipeUrl) && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Original Recipe</h3>
                    <p className="text-sm text-gray-600">View the full recipe with detailed instructions</p>
                  </div>
                </div>
                <Button
                  onClick={() => window.open(recipeDetails.sourceUrl || recipeDetails.recipeUrl, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  View Recipe
                </Button>
              </div>
            </div>
          )}
          
          <div className="rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6">
            <div className="mb-3 flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-slate-800" />
              <h3 className="text-[16px] font-bold text-slate-900 sm:text-[18px]">Instructions:</h3>
            </div>
            <ul className="space-y-2">
              {parsedInstructions.map((instruction, index) => (
                <li
                  key={index}
                  className="flex gap-2.5 text-[14px] font-normal leading-relaxed text-[#364153]"
                >
                  <span
                    className="flex h-[1lh] w-2 shrink-0 items-center justify-center"
                    aria-hidden
                  >
                    <span className="block h-1.5 w-1.5 rounded-full bg-slate-400" />
                  </span>
                  <span className="min-w-0">
                    {typeof instruction.step === 'string' ? instruction.step.replace(/^- /, '') : String(instruction.step)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Notes — outlined floating label, same as Servings in RecipeDetailsModal */}
          <div className="space-y-2">
            <NotesOutlinedField
              id="recipe-notes-field"
              labelId="recipe-notes-label"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesFeedback(null);
              }}
              loading={loadingNotes}
              placeholder="Add your personal notes, modifications, or tips for this recipe..."
            />
            <div className="flex justify-end pt-1">
              <Button
                onClick={handleSaveNotes}
                disabled={saving || !notes.trim() || loadingNotes}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Notes'
                )}
              </Button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          {/* Source Link Section - Even when no instructions */}
          {(recipeDetails.sourceUrl || recipeDetails.recipeUrl) && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Original Recipe</h3>
                    <p className="text-sm text-gray-600">View the full recipe with detailed instructions</p>
                  </div>
                </div>
                <Button
                  onClick={() => window.open(recipeDetails.sourceUrl || recipeDetails.recipeUrl, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  View Recipe
                </Button>
              </div>
            </div>
          )}

          <div className="rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6">
            <div className="mb-3 flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-slate-800" />
              <h3 className="text-[16px] font-bold text-slate-900 sm:text-[18px]">Instructions:</h3>
            </div>
            <p className="text-[14px] font-normal text-[#364153]">No cooking instructions added.</p>
          </div>

          {/* Notes — outlined floating label, same as Servings in RecipeDetailsModal */}
          <div className="space-y-2">
            <NotesOutlinedField
              id="recipe-notes-field-empty-instructions"
              labelId="recipe-notes-label-empty-instructions"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesFeedback(null);
              }}
              loading={loadingNotes}
              placeholder="Add your personal notes, modifications, or tips for this recipe..."
            />
            <div className="flex justify-end pt-1">
              <Button
                onClick={handleSaveNotes}
                disabled={saving || !notes.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Notes'
                )}
              </Button>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        {notesFeedback ? (
          <InlineFeedback
            variant="error"
            title="Unable to save notes"
            message={notesFeedback}
            className="mb-4"
          />
        ) : null}
        {renderInstructions()}
      </CardContent>
    </Card>
  );
};

export default InstructionsTab;
